import { TYPOGRAPHY, DEVICE } from '../../../assets/styles';
import { CrossIcon } from '../../../assets/svg';
import React, { FC, ReactNode, useRef } from 'react';
import { FieldRenderProps } from 'react-final-form';
import styled from 'styled-components/macro';

interface InputProps extends FieldRenderProps<string | number | undefined> {
  className?: string;
  label?: ReactNode;
  disabled?: boolean;
  placeholder?: string;
  patter?: string;
  hideError?: boolean;
  suffix?: string;
  unit?: string;
}

export const Input: FC<InputProps> = ({ className, label, disabled, placeholder, input, meta, pattern, hideError, suffix, unit }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapRef = useRef<HTMLDivElement>(null);

  const hasError = meta.touched && meta.error && !hideError;

  const handleInputRootClick = () => {
    inputRef.current && inputRef.current.focus();
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    input.onChange('');
  };

  const onWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (input.type === 'number') {
      e.currentTarget.blur();
    }
  };

  return (
    <Root className={className}>
      {label && <StyledLabel>{label}</StyledLabel>}
      <InputWrap ref={inputWrapRef} $hasError={hasError} onClick={handleInputRootClick}>
        <StyledInput
          ref={inputRef}
          pattern={pattern}
          {...input}
          disabled={disabled}
          placeholder={placeholder}
          onWheel={onWheel}
        />
        {suffix && !!input.value && <SuffixText>{suffix}</SuffixText>}
        {unit && !input.value && <UnitText>{unit}</UnitText>}
        {!!input.value && !disabled && (
          <ClearButton tabIndex={-1} type="button" onClick={handleClear}>
            <CrossIcon />
          </ClearButton>
        )}
        {hasError && <ErrorText>{meta.error}</ErrorText>}
      </InputWrap>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
`;

const StyledInput = styled.input`
  ${TYPOGRAPHY.bodyRegular16}
  width: 80%;
  display: block;
  color: var(--color6);
  outline: none;

  &::placeholder {
    color: var(--color9);
  }
`;

const InputWrap = styled.div<{ $hasError: boolean }>`
  position: relative;
  width: 100%;
  height: 56px;
  padding: 0 16px;
  display: flex;
  align-items: center;

  border-radius: 12px;
  border: 1px solid var(--color10);
  ${({ $hasError }) =>
    $hasError &&
    `border: 1px solid var(--color12);
  `}

  background-color: var(--color5);
  cursor: text;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    border-color: ${({ $hasError }) => ($hasError ? 'var(--color12)' : 'var(--color9)')};
  }

  &:focus-within {
    border: 1px solid var(--color2);
    box-shadow: 0 0 0 3px rgba(206, 17, 38, 0.08);
  }

  @media ${DEVICE.laptop} {
    margin-bottom: 20px;
  }
`;

const StyledLabel = styled.label`
  ${TYPOGRAPHY.captionRegular14};
  display: block;
  margin-bottom: 6px;
  color: var(--color9);
  white-space: pre-line;
`;

const SuffixText = styled.span`
  ${TYPOGRAPHY.bodyRegular16}
  color: var(--color9);
  pointer-events: none;
  white-space: nowrap;
  user-select: none;
  padding-left: 4px;
`;

const UnitText = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color7);
  pointer-events: none;
  white-space: nowrap;
  user-select: none;
  margin-left: auto;
`;

const ClearButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const ErrorText = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  color: var(--color12);
`;
